import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, RefreshCw, Loader2, AlertTriangle, Info, AlertOctagon, Wrench, Package, ShoppingCart, ArrowRightLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  info:     { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   icon: Info,          iconColor: 'text-blue-500' },
  warning:  { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  icon: AlertTriangle, iconColor: 'text-amber-500' },
  critical: { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    icon: AlertOctagon,  iconColor: 'text-red-500' },
};

const CATEGORY_ICONS = {
  maintenance:      Wrench,
  inventory:        Package,
  procurement:      ShoppingCart,
  cannibalization:  ArrowRightLeft,
};

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function AIInsightsCard() {
  const [insights, setInsights] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = async (force = false) => {
    try {
      if (force) setRefreshing(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/ai/insights${force ? '?force=true' : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(res.data.insights || []);
      setGeneratedAt(res.data.generated_at);
      setCached(res.data.cached);
    } catch {
      // silent — AI may be unavailable
      setInsights([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchInsights(); }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50 via-white to-slate-50 p-5 animate-pulse">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-amber-500" />
          <span className="font-semibold text-slate-700">Preparing today's briefing…</span>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-slate-200 rounded w-3/4" />
          <div className="h-3 bg-slate-200 rounded w-2/3" />
          <div className="h-3 bg-slate-200 rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ai-insights-card">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-amber-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Operations Briefing</h3>
          </div>
          <button onClick={() => fetchInsights(true)} disabled={refreshing}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
            data-testid="ai-insights-refresh" title="Regenerate">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-sm text-slate-500">Nothing notable to report — operations look healthy this morning.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50/70 via-white to-slate-50 p-5 shadow-sm" data-testid="ai-insights-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <Sparkles className="h-4.5 w-4.5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 leading-tight">Operations Briefing</h3>
            <p className="text-xs text-slate-500">
              AI-generated insights · {generatedAt ? `last updated ${formatTime(generatedAt)}` : 'today'}
              {cached && <span className="ml-1 text-[10px] text-slate-400">(cached)</span>}
            </p>
          </div>
        </div>
        <button onClick={() => fetchInsights(true)} disabled={refreshing}
          className="text-slate-500 hover:text-amber-600 p-1.5 rounded-lg hover:bg-white/60 transition-colors"
          data-testid="ai-insights-refresh" title="Regenerate briefing">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {insights.map((ins, i) => {
          const sev = SEVERITY_STYLES[ins.severity] || SEVERITY_STYLES.info;
          const CatIcon = CATEGORY_ICONS[ins.category] || Sparkles;
          const SevIcon = sev.icon;
          return (
            <div
              key={i}
              data-testid={`ai-insight-${i}`}
              className={`rounded-xl border ${sev.border} ${sev.bg} p-3 hover:shadow-sm transition-shadow`}
            >
              <div className="flex items-start gap-2">
                <div className={`flex-shrink-0 h-7 w-7 rounded-md bg-white border ${sev.border} flex items-center justify-center`}>
                  <CatIcon className={`h-3.5 w-3.5 ${sev.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <SevIcon className={`h-3 w-3 ${sev.iconColor}`} />
                    <p className={`text-xs font-bold ${sev.text} uppercase tracking-wide`}>{ins.title}</p>
                  </div>
                  <p className="text-sm text-slate-700 leading-snug">{ins.message}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
